// CARO. Casa de estilo — client cart state (UX only).
// Persisted to localStorage for convenience. NOT authoritative: the Worker
// recomputes price/VAT/availability/totals at checkout from D1.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { Pack, Product, ProductCategory, PublicPack } from '@/types';
import { track } from '@/client/lib/analytics';
import { lineKey, productIdOfLineKey, sizeUnits } from '@/lib/cart';
import { MAX_PACK_QUANTITY, maxPacksForSizes, packNameEs } from '@/lib/catalog/packs';

export interface CartSnapshot {
  id: string;
  slug: string;
  name: string;
  brand: string | null;
  priceCents: number;
  currency: string;
  image: string | null;
  category: string;
  /**
   * Availability captured when the line was added/refreshed: raw stock minus
   * units soft-reserved by OTHER carts (own holds excluded). Used to cap the
   * quantity controls. The Worker re-validates authoritatively at checkout.
   * For a sized line it is that SIZE's availability (capped by the product
   * total), so the line can't exceed the units of the chosen size.
   */
  stockQuantity: number;
  inventoryTracking: boolean;
  /**
   * Pre-order (reasortimiento): the piece is bought before CARO has stock, so
   * the quantity cap ignores `stockQuantity` (treated as open stock). The Worker
   * re-validates authoritatively at checkout.
   */
  preorder?: boolean;
  /**
   * Chosen size for a sized product (`metadata.sizes`); absent for unsized ones
   * (and for every line stored before sizes existed). Part of the line key.
   */
  size?: string;
}

/** A product line (`kind` absent on every line stored before packs existed). */
export interface ProductCartItem {
  kind?: 'product';
  product: CartSnapshot;
  quantity: number;
}

/** One component of a pack line, as captured when the pack was added. */
export interface PackComponentSnapshot {
  id: string;
  slug: string;
  category: string;
  /** Localized name at add time (like CartSnapshot.name). */
  name: string;
  image: string | null;
  /** Units of this product in ONE pack. */
  quantity: number;
  /** Chosen size (sized components), the same for all its units. */
  size?: string;
}

/**
 * A pack line's snapshot (docs/plan-packs.md). Display only: the price is the
 * one the API returned (never computed here) and the Worker re-prices the line
 * from D1 at quote/checkout. The name is re-derived per locale at render
 * (`packName`), from the same fields the server uses.
 */
export interface PackSnapshot {
  id: string;
  slug: string;
  name: string | null;
  i18n: Pack['i18n'];
  discountPercent: number;
  primaryCategory: ProductCategory;
  seq: number;
  /** Pack price from the API (x,99). */
  priceCents: number;
  /** Sum of the components' prices («antes»), from the API. */
  compareAtCents: number;
  currency: string;
  /** Own photo; null → collage of the components' covers. */
  image: string | null;
  components: PackComponentSnapshot[];
  /** Client cap for this line (maxPacksForSizes when added/refreshed). */
  maxQty: number;
}

export interface PackCartItem {
  kind: 'pack';
  pack: PackSnapshot;
  quantity: number;
}

export type CartItem = ProductCartItem | PackCartItem;

interface CartState {
  items: CartItem[];
}

type Action =
  | { type: 'add'; product: CartSnapshot; quantity: number }
  | { type: 'addPack'; pack: PackSnapshot; quantity: number }
  | { type: 'setQty'; key: string; quantity: number }
  | { type: 'remove'; key: string }
  | { type: 'clear' }
  | { type: 'hydrate'; items: CartItem[] };

const STORAGE_KEY = 'caro.cart.v1';
/** Hard ceiling per line for non-tracked items (e.g. beauty with open stock). */
export const MAX_QTY = 20;

/** True for a pack line. */
export function isPackItem(item: CartItem): item is PackCartItem {
  return item.kind === 'pack';
}

/** True for a product line (the complement of isPackItem, usable as a filter guard). */
export function isProductItem(item: CartItem): item is ProductCartItem {
  return item.kind !== 'pack';
}

/** Chosen sizes of a pack line (productId → size), {} when none. */
export function packItemSizes(pack: PackSnapshot): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of pack.components) if (c.size) out[c.id] = c.size;
  return out;
}

/**
 * Line key of a pack line: `pack:<id>`, plus `#<productId>=<size>,…` (sorted by
 * product id) when sizes are chosen, so the same pack in other sizes is its own
 * line. Never collides with a product line key (SLUG_RE has no `:`).
 */
export function packLineKey(packId: string, sizes: Readonly<Record<string, string>> = {}): string {
  const parts = Object.keys(sizes)
    .filter((id) => sizes[id])
    .sort()
    .map((id) => `${id}=${sizes[id]}`);
  return parts.length ? `pack:${packId}#${parts.join(',')}` : `pack:${packId}`;
}

/** Line key of a cart item: product id, `id#size` for a sized line, `pack:<id>[#sizes]` for a pack. */
export function itemKey(item: CartItem): string {
  return isPackItem(item)
    ? packLineKey(item.pack.id, packItemSizes(item.pack))
    : lineKey(item.product.id, item.product.size);
}

/**
 * Whether a soldOut / backorder entry (or a quote issue) from the Worker
 * concerns this line:
 * - with `packId` (a pack component, or the whole pack when productId is ''):
 *   the lines of that pack, narrowed to the chosen size when the entry has one;
 * - otherwise, product lines only: the exact (product, size) line, or every
 *   line of the product when the entry has no size (a product-level issue).
 *   An entry without a product id never matches a loose line.
 */
export function entryMatchesItem(
  entry: { productId: string; size?: string; packId?: string },
  item: CartItem,
): boolean {
  if (entry.packId) {
    if (!isPackItem(item) || item.pack.id !== entry.packId) return false;
    if (!entry.size) return true;
    return item.pack.components.some((c) => c.id === entry.productId && c.size === entry.size);
  }
  if (isPackItem(item) || !entry.productId) return false;
  const key = itemKey(item);
  return entry.size
    ? lineKey(entry.productId, entry.size) === key
    : productIdOfLineKey(key) === entry.productId;
}

/**
 * Availability to snapshot for a (product, size) line: the product's (already
 * reservation-adjusted) stock, and for a sized product with `sizeStock` also
 * that size's units. Pre-orders ignore it (open ceiling in `maxForSnapshot`).
 */
export function availableForSize(product: Product, size?: string): number {
  const total = product.stockQuantity;
  if (!size) return total;
  const units = sizeUnits(product, size);
  return units === null ? total : Math.min(total, units);
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object';
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;
const CATEGORIES: readonly string[] = ['beauty', 'estilo', 'casa'] satisfies ProductCategory[];

function isProductEntry(i: Record<string, unknown>): boolean {
  const product = i.product;
  return (
    (i.kind === undefined || i.kind === 'product') &&
    isObject(product) &&
    typeof product.id === 'string' &&
    typeof i.quantity === 'number' &&
    (product.size === undefined || typeof product.size === 'string')
  );
}

function isComponentSnapshot(c: unknown): boolean {
  return (
    isObject(c) &&
    typeof c.id === 'string' &&
    c.id !== '' &&
    typeof c.slug === 'string' &&
    typeof c.category === 'string' &&
    typeof c.name === 'string' &&
    (c.image === null || typeof c.image === 'string') &&
    isCount(c.quantity) &&
    (c.size === undefined || (typeof c.size === 'string' && c.size !== ''))
  );
}

function isPackEntry(i: Record<string, unknown>): boolean {
  const p = i.pack;
  return (
    isCount(i.quantity) &&
    isObject(p) &&
    typeof p.id === 'string' &&
    p.id !== '' &&
    typeof p.slug === 'string' &&
    (p.name === null || typeof p.name === 'string') &&
    isObject(p.i18n) &&
    typeof p.discountPercent === 'number' &&
    typeof p.primaryCategory === 'string' &&
    CATEGORIES.includes(p.primaryCategory) &&
    typeof p.seq === 'number' &&
    typeof p.priceCents === 'number' &&
    Number.isFinite(p.priceCents) &&
    typeof p.compareAtCents === 'number' &&
    typeof p.currency === 'string' &&
    (p.image === null || typeof p.image === 'string') &&
    typeof p.maxQty === 'number' &&
    Array.isArray(p.components) &&
    p.components.length > 0 &&
    p.components.every(isComponentSnapshot)
  );
}

/**
 * Stored lines from localStorage, tolerating old (v1: product-only, sizeless)
 * carts; malformed product or pack entries are dropped.
 */
export function parseStored(raw: string): CartItem[] {
  const data: unknown = JSON.parse(raw);
  if (!Array.isArray(data)) return [];
  return data.filter(
    (i): i is CartItem => isObject(i) && (i.kind === 'pack' ? isPackEntry(i) : isProductEntry(i)),
  );
}

/** Max units allowed for a line: capped by stock (pre-orders use the open ceiling). */
export function maxForSnapshot(snap: CartSnapshot): number {
  // Stock-based for every product; only pre-order (reasortimiento) pieces sell
  // before stock arrives → open ceiling. There is no "untracked / unlimited" mode.
  if (!snap.preorder) {
    return Math.max(0, Math.min(snap.stockQuantity, MAX_QTY));
  }
  return MAX_QTY;
}

/** Max packs allowed for a pack line (its snapshot cap, never above MAX_PACK_QUANTITY). */
export function maxForPack(pack: PackSnapshot): number {
  return Math.max(0, Math.min(pack.maxQty, MAX_PACK_QUANTITY));
}

/** Max quantity of any cart line. */
export function maxForItem(item: CartItem): number {
  return isPackItem(item) ? maxForPack(item.pack) : maxForSnapshot(item.product);
}

/**
 * Snapshot of a pack for a cart line: components in pack order with the chosen
 * sizes (only for components of the pack), price as the API returned it, and
 * the line cap (maxPacksForSizes).
 */
export function packSnapshot(pack: PublicPack, sizes: Readonly<Record<string, string>> = {}): PackSnapshot {
  const byId = new Map(pack.components.map((c) => [c.id, c]));
  const chosen: Record<string, string> = {};
  const components: PackComponentSnapshot[] = [];
  for (const item of pack.items) {
    const c = byId.get(item.productId);
    if (!c) continue;
    const size = Object.prototype.hasOwnProperty.call(sizes, c.id) ? sizes[c.id] : undefined;
    if (size) chosen[c.id] = size;
    components.push({
      id: c.id,
      slug: c.slug,
      category: c.category,
      name: c.name,
      image: c.image,
      quantity: item.quantity,
      ...(size ? { size } : {}),
    });
  }
  return {
    id: pack.id,
    slug: pack.slug,
    name: pack.name,
    i18n: pack.i18n,
    discountPercent: pack.discountPercent,
    primaryCategory: pack.primaryCategory,
    seq: pack.seq,
    priceCents: pack.priceCents,
    compareAtCents: pack.compareAtCents,
    currency: pack.components[0]?.currency ?? 'EUR',
    image: pack.image,
    components,
    maxQty: maxPacksForSizes(pack, pack.components, chosen),
  };
}

function reducer(state: CartState, action: Action): CartState {
  switch (action.type) {
    case 'hydrate':
      return { items: action.items };
    case 'add': {
      const key = lineKey(action.product.id, action.product.size);
      const existing = state.items.find(
        (i): i is ProductCartItem => isProductItem(i) && itemKey(i) === key,
      );
      if (existing) {
        // Refresh the availability snapshot with the latest known value.
        const snap = { ...existing.product, ...action.product };
        const cap = maxForSnapshot(snap);
        return {
          items: state.items.map((i) =>
            i === existing
              ? { ...existing, product: snap, quantity: Math.min(i.quantity + action.quantity, cap) }
              : i,
          ),
        };
      }
      return {
        items: [
          ...state.items,
          { product: action.product, quantity: Math.min(action.quantity, maxForSnapshot(action.product)) },
        ],
      };
    }
    case 'addPack': {
      const key = packLineKey(action.pack.id, packItemSizes(action.pack));
      const cap = maxForPack(action.pack);
      const existing = state.items.find((i) => isPackItem(i) && itemKey(i) === key);
      if (existing) {
        // Refresh the snapshot (price, cap) with the latest API values.
        return {
          items: state.items
            .map((i): CartItem =>
              i === existing
                ? { kind: 'pack', pack: action.pack, quantity: Math.min(i.quantity + action.quantity, cap) }
                : i,
            )
            .filter((i) => i.quantity > 0),
        };
      }
      const quantity = Math.min(action.quantity, cap);
      if (quantity < 1) return state;
      return { items: [...state.items, { kind: 'pack', pack: action.pack, quantity }] };
    }
    case 'setQty':
      return {
        items: state.items
          .map((i) =>
            itemKey(i) === action.key
              ? { ...i, quantity: Math.max(0, Math.min(action.quantity, maxForItem(i))) }
              : i,
          )
          .filter((i) => i.quantity > 0),
      };
    case 'remove':
      return { items: state.items.filter((i) => itemKey(i) !== action.key) };
    case 'clear':
      return { items: [] };
    default:
      return state;
  }
}

interface CartContextValue {
  items: CartItem[];
  count: number;
  subtotalCents: number;
  currency: string;
  /** `size` is required for sized products (the PDP enforces it). */
  add: (product: Product, quantity?: number, size?: string) => void;
  /**
   * Adds a pack as the API returned it (components already localized for
   * display), with one size per sized component (`productId → size`).
   */
  addPack: (pack: PublicPack, quantity?: number, sizes?: Record<string, string>) => void;
  /** By line key (`itemKey`). */
  setQty: (key: string, quantity: number) => void;
  remove: (key: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { items: [] });
  // The reducer starts EMPTY and the real cart arrives one render later, from
  // localStorage. Everything downstream has to wait for that: persisting the
  // empty state would wipe the stored cart, and the heartbeat would release the
  // holds this cart already owns before recreating them a moment later — two D1
  // writes per page load instead of one. See ../../../docs/revision-codigo.md C4.
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage once.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      // v1 lines have no `size` nor `kind`: their key is the bare product id, as before.
      if (raw) dispatch({ type: 'hydrate', items: parseStored(raw) });
    } catch {
      /* ignore corrupt cart */
    }
    setHydrated(true);
  }, []);

  // Persist on change (never before hydrating — see above).
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch {
      /* storage may be unavailable */
    }
  }, [state.items, hydrated]);

  // No browsing-time reservation: stock is reserved only at checkout (transmit),
  // so the cart never creates "phantom" holds. See the checkout route + docs.

  const add = useCallback((product: Product, quantity = 1, size?: string) => {
    dispatch({
      type: 'add',
      quantity,
      product: {
        id: product.id,
        slug: product.slug,
        name: product.name,
        brand: product.brand,
        priceCents: product.priceCents,
        currency: product.currency,
        image: product.image,
        category: product.category,
        stockQuantity: availableForSize(product, size),
        inventoryTracking: product.inventoryTracking,
        preorder: product.metadata.preorder === true,
        ...(size ? { size } : {}),
      },
    });
    track('add_to_cart', { id: product.id, name: product.name, ...(size ? { size } : {}) });
  }, []);

  const addPack = useCallback(
    (pack: PublicPack, quantity = 1, sizes: Record<string, string> = {}) => {
      dispatch({ type: 'addPack', quantity, pack: packSnapshot(pack, sizes) });
      track('add_to_cart', { id: `pack:${pack.id}`, name: packNameEs(pack) });
    },
    [],
  );

  const setQty = useCallback(
    (key: string, quantity: number) => dispatch({ type: 'setQty', key, quantity }),
    [],
  );
  const remove = useCallback((key: string) => dispatch({ type: 'remove', key }), []);
  const clear = useCallback(() => dispatch({ type: 'clear' }), []);

  const value = useMemo<CartContextValue>(() => {
    const count = state.items.reduce((n, i) => n + i.quantity, 0);
    // Display only: a pack's price is the API's; the Worker re-prices everything.
    const subtotalCents = state.items.reduce(
      (n, i) => n + (isPackItem(i) ? i.pack.priceCents : i.product.priceCents) * i.quantity,
      0,
    );
    const first = state.items[0];
    const currency = first ? (isPackItem(first) ? first.pack.currency : first.product.currency) : 'EUR';
    return { items: state.items, count, subtotalCents, currency, add, addPack, setQty, remove, clear };
  }, [state.items, add, addPack, setQty, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
